import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  /** Accessible name for the trigger, e.g. "About visibility". */
  label: string;
  /** The explanation shown when the affordance is opened. */
  children: React.ReactNode;
  className?: string;
};

/** Small "ⓘ" affordance revealing a short metric explanation. Built on Radix
 *  Popover (not Tooltip) so it works on TOUCH. Behaviour by input:
 *   - Desktop mouse: opens on HOVER, closes on leave (the pointer handlers,
 *     gated to `pointerType === "mouse"`).
 *   - Touch: a tap goes through Radix's own trigger toggle (the mouse-gated
 *     hover handlers no-op on touch), and outside-tap / Escape close it.
 *   - Keyboard: focus + Enter/Space toggles, Escape closes (Radix).
 *  Portaled with `.vb-root` so design tokens resolve. */
export function InfoPopover({ label, children, className }: Props) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn("vb-info", className)}
          aria-label={label}
          onPointerEnter={(e) => {
            if (e.pointerType === "mouse") setOpen(true);
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === "mouse") setOpen(false);
          }}
        >
          <Info aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="vb-root vb-info-pop"
          sideOffset={6}
          collisionPadding={10}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {children}
          <Popover.Arrow className="vb-info-pop__arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default InfoPopover;
